/**
 * Per Company mapping to a vendor side external company (e.g. one Aumico Company
 * linked to one Business Central company). Persisted on the Company document
 * under `integrations.connectionMappings`. The integrations lib does not own
 * the Company document: it only owns the read/write of these mappings.
 *
 * Wire format mirrors `@modeso/types__aumico-lib-companies-be#IConnectionMapping`
 * verbatim: keep these in sync.
 */

import { IntegrationVendor } from './integration-vendor.enum';

/**
 * Vendors that can own a ConnectionMapping on a Company. Bexio is intentionally
 * absent — it has its own per Company integration (`integrations.bexio`), not a
 * `connectionMappings[]` entry. Use this union to switch UI in any "linked to"
 * / "broken mapping" affordance rather than hard coding a single vendor.
 */
export type ConnectionMappingVendor =
    | IntegrationVendor.D365_BC
    | IntegrationVendor.ABACUS
    | IntegrationVendor.TOPAL
    | IntegrationVendor.PROFFIX
    | IntegrationVendor.ACCOUNTO;

export type ConnectionMappingStatus = 'ACTIVE' | 'DISABLED' | 'ERROR';

export interface IConnectionMapping {
    vendor: ConnectionMappingVendor;
    /** ObjectId of the IntegrationConnection this mapping points at. Stable across re connects. */
    connectionId: string;
    /** Vendor side ID: BC company GUID, Abacus mandant ID, etc. */
    externalCompanyId: string;
    /** Cached display name at mapping time: used when the connection is unreachable. */
    externalCompanyName: string;
    /** Mapping lifecycle. ACTIVE on first upsert; flipped server side when the connection breaks. */
    status: ConnectionMappingStatus;
    /** ISO 8601 timestamp (string), set server side on every upsert. */
    mappedAt: string;
}

/**
 * Aumico Company decorated with its connection mapping. Returned by
 * `GET /api/companies/by-integration-connection/:connectionId`: used to render
 * the "X Aumico Companies linked to this BC tenant" list in the manage modal.
 */
export interface IAumicoCompanyWithMapping {
    id: string;
    name: string;
    mapping: IConnectionMapping;
}

/**
 * Payload shape for `PUT /api/companies/:cId/integrations/mappings`. The server
 * authoritatively fills `status` (always ACTIVE) and `mappedAt` (always now) —
 * clients MUST omit both.
 */
export type IUpsertConnectionMappingBody = Omit<IConnectionMapping, 'status' | 'mappedAt'>;

// ── Bulk upsert (used by auto match after first Connect) ─────────────────────

/**
 * Per item entry in the bulk upsert request body. The vendor + connectionId
 * are pulled up to the envelope to save bytes (cap is 200 per call).
 *
 * `POST /api/companies/connection-mappings/bulk-upsert`
 */
export interface IBulkUpsertMappingItem {
    companyId: string;
    externalCompanyId: string;
    externalCompanyName: string;
}

export interface IBulkUpsertMappingsBody {
    vendor: ConnectionMappingVendor;
    connectionId: string;
    mappings: IBulkUpsertMappingItem[];
}

/** Per item outcome. The endpoint allows partial success: one row failing does not abort the batch. */
export type IBulkUpsertMappingResult =
    | {
          companyId: string;
          status: 'ok';
          mapping: IConnectionMapping;
      }
    | {
          companyId: string;
          status: 'error';
          /** Today only `COMPANY_NOT_FOUND`. Treat as an open string so new codes don't break the FE. */
          errorCode: string;
          errorMessage: string;
      };

export interface IBulkUpsertMappingsResponse {
    summary: { ok: number; errors: number; total: number };
    results: IBulkUpsertMappingResult[];
}
